import { cashfreeService } from "@/services/paymentServices";
import {
  paymentStateProps,
  PaymentState,
  ApplyCoupon,
  ApplyWallet,
  ApplyCredit,
  CreateOrder,
  ActivateLOC,
  PaymentSummary,
} from "@/types/payment";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Initial State
const initialState: paymentStateProps = {
  useWalletAmount: 0,
  useCreditAmount: 0,
  selectedCoupon: {
    code: "",
    couponDiscountPercentage: "",
    couponDiscountTaken: 0,
  },
  walletBalance: null,
  availableCoupons: [],
  generatePaymentUrl: false,
  openPaymentModel: false,
  paymentData: null,
  paymentStatus: null,
  availableCredit: null,
  creditPageDetails: null,
  paymentSummary: null,
  wallet: null,
  loading: false,
  getPaymentSummaryLoading: false,
  postPaymentSummaryLoading: false,
  error: null,
};

// ✅ Fetch Coupons API
export const fetchCoupons = createAsyncThunk(
  "cashfree/fetchCoupons",
  async (_, { rejectWithValue }) => {
    try {
      return await cashfreeService.fetchCoupons();
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching coupons");
    }
  }
);

// ✅ Validate Coupon API
export const validateCoupon = createAsyncThunk(
  "cashfree/validateCoupon",
  async (
    { userID, couponCode }: { userID: string | null; couponCode: string },
    { rejectWithValue }
  ) => {
    try {
      return await cashfreeService.validateCoupon(userID, couponCode);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error validating coupon");
    }
  }
);

// ✅ Initiate Payment API
export const initiatePayment = createAsyncThunk(
  "cashfree/initiatePayment",
  async (
    { userID, amount }: { userID: string; amount: number },
    { rejectWithValue }
  ) => {
    try {
      return await cashfreeService.initiatePayment(userID, amount);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error initiating payment");
    }
  }
);

// ✅ Check Payment Status API
export const checkPaymentStatus = createAsyncThunk(
  "cashfree/checkPaymentStatus",
  async (linkID: string, { rejectWithValue }) => {
    try {
      return await cashfreeService.checkPaymentStatus(linkID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error checking payment status");
    }
  }
);

// ✅ Fetch Available Credit API
export const fetchAvailableCredit = createAsyncThunk(
  "cashfree/fetchAvailableCredit",
  async (userID: string | null, { rejectWithValue }) => {
    try {
      return await cashfreeService.fetchAvailableCredit(userID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching available credit");
    }
  }
);

// ✅ Fetch Credit Page API
export const fetchCreditPage = createAsyncThunk(
  "cashfree/fetchCreditPage",
  async (userID: string | null, { rejectWithValue }) => {
    try {
      return await cashfreeService.fetchCreditPage(userID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching credit page details");
    }
  }
);
// ✅ Fetch Wallet Balance API
export const fetchWalletBalance = createAsyncThunk(
  "cashfree/fetchWalletBalance",
  async (userID: string | null, { rejectWithValue }) => {
    try {
      return await cashfreeService.fetchWalletBalance(userID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching credit page details");
    }
  }
);

// ✅ Fetch Wallet Balance API
export const fetchWallet = createAsyncThunk(
  "cashfree/fetchWallet",
  async (userID: string | null, { rejectWithValue }) => {
    try {
      return await cashfreeService.fetchWallet(userID);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching wallet details");
    }
  }
);

// ✅ Apply Coupon code
export const applyCoupon = createAsyncThunk(
  "cashfree/applyCoupon",
  async (data: ApplyCoupon, { rejectWithValue }) => {
    try {
      return await cashfreeService.applyCoupon(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while applying coupon"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Apply Wallet
export const applyWallet = createAsyncThunk(
  "cashfree/applyWallet",
  async (data: ApplyWallet, { rejectWithValue }) => {
    try {
      return await cashfreeService.applyWallet(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while applying wallet"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Apply Credit
export const applyCredit = createAsyncThunk(
  "cashfree/applyCredit",
  async (data: ApplyCredit, { rejectWithValue }) => {
    try {
      return await cashfreeService.applyCredit(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while applying credit"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Create Order
export const createOrder = createAsyncThunk(
  "cashfree/createOrder",
  async (data: CreateOrder, { rejectWithValue }) => {
    try {
      return await cashfreeService.createOrder(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while creating order"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Activate LOC
export const activateLOC = createAsyncThunk(
  "cashfree/activateLOC",
  async (data: ActivateLOC, { rejectWithValue }) => {
    try {
      return await cashfreeService.activateLOC(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while activating LOC"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Post Payment Summary
export const postPaymentSummary = createAsyncThunk(
  "cashfree/postPaymentSummary",
  async (data: PaymentSummary, { rejectWithValue }) => {
    try {
      return await cashfreeService.postPaymentSummary(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while activating LOC"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Get Payment Summary
export const getPaymentSummary = createAsyncThunk(
  "cashfree/getPaymentSummary",
  async (phoneNumber: string | null, { rejectWithValue }) => {
    try {
      return await cashfreeService.getPaymentSummary(phoneNumber);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data || "Error while activating LOC"
        );
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

// ✅ Fetch Coupons API
export const updatePaymentStatusByAdmin = createAsyncThunk(
  "cashfree/updatePaymentStatusByAdmin",
  async (transactionId: string | null, { rejectWithValue }) => {
    try {
      return await cashfreeService.updatePaymentStatusByAdmin(transactionId);
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Error fetching coupons");
    }
  }
);

// ✅ Cashfree Payment Slice
const paymentSlice = createSlice({
  name: "cashfree",
  initialState,
  reducers: {
    updatePaymentData: (
      state,
      action: PayloadAction<Partial<PaymentState>>
    ) => {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.availableCoupons = action.payload;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(validateCoupon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateCoupon.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(validateCoupon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(initiatePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiatePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentData = action.payload;
      })
      .addCase(initiatePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(checkPaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPaymentStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentStatus = action.payload;
      })
      .addCase(checkPaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAvailableCredit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableCredit.fulfilled, (state, action) => {
        state.loading = false;
        state.availableCredit = action.payload;
      })
      .addCase(fetchAvailableCredit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCreditPage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditPage.fulfilled, (state, action) => {
        state.loading = false;
        state.creditPageDetails = action.payload;
      })
      .addCase(fetchCreditPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWalletBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.walletBalance = action.payload;
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWallet.fulfilled, (state, action) => {
        state.loading = false;
        state.wallet = action.payload;
      })
      .addCase(fetchWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(applyCoupon.fulfilled, () => {})
      .addCase(applyWallet.fulfilled, () => {})
      .addCase(applyCredit.fulfilled, () => {})
      .addCase(createOrder.fulfilled, () => {})
      .addCase(activateLOC.fulfilled, () => {})
      .addCase(postPaymentSummary.pending, (state) => {
        state.postPaymentSummaryLoading = true;
      })
      .addCase(postPaymentSummary.fulfilled, (state) => {
        state.postPaymentSummaryLoading = false;
      })
      .addCase(getPaymentSummary.pending, (state) => {
        state.getPaymentSummaryLoading = true;
      })
      .addCase(getPaymentSummary.fulfilled, (state, action) => {
        state.getPaymentSummaryLoading = false;
        state.paymentSummary = action.payload;
      })
      .addCase(updatePaymentStatusByAdmin.fulfilled, () => {});
  },
});

export const { updatePaymentData } = paymentSlice.actions;
export default paymentSlice.reducer;
